<template>
  <modal :title="modalTitle" @close="$emit('close')">
    <div class="approval-modal-content">
      <p v-if="deliveryDateMessage" class="delivery-date-notice">
        {{ deliveryDateMessage }}
      </p>

      <div class="reason-input">
        <label for="approval-reason">
          {{ reasonLabel }}
          <span v-if="isReasonRequired" class="required">*</span>
        </label>
        <textarea
          id="approval-reason"
          v-model="reason"
          :placeholder="reasonPlaceholder"
          :required="isReasonRequired"
          rows="4"
          :class="{ error: showError }"
        />
        <div v-if="showError" class="error-message">
          {{ i18n.t('/ManagerApproval/Modal/ReasonRequired') }}
        </div>
      </div>
    </div>

    <div class="modal-actions">
      <app-button
        data-qa="approval-modal-cancel"
        button-type="secondary"
        :disabled="isProcessing"
        @click="$emit('close')"
      >
        {{ i18n.t('/Common/Cancel') }}
      </app-button>

      <app-button
        :data-qa="`approval-modal-${type}`"
        :button-type="type === 'approve' ? 'primary' : 'danger'"
        :disabled="isProcessing || (isReasonRequired && !reason.trim())"
        @click="handleConfirm"
      >
        <span v-if="isProcessing">{{ i18n.t('/Common/Processing') }}</span>
        <span v-else>{{ confirmButtonText }}</span>
      </app-button>
    </div>
  </modal>
</template>
<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import { mapState } from 'vuex'
import AdditionalOrder from '@/mixins/additionalOrder'

export default {
  name: 'ApprovalModal',
  components: {
    Modal,
    AppButton,
  },
  mixins: [AdditionalOrder],
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['approve', 'reject'].includes(value),
    },
    deliveryDateChanged: {
      type: Boolean,
      default: false,
    },
    newDeliveryDate: {
      type: String,
      default: null,
    },
    isProcessing: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'confirm'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      reason: '',
      showError: false,
    }
  },
  computed: {
    ...mapState({
      quote(state, getters) {
        return getters[this.quoteNamespace + '/get']
      },
    }),
    quoteStatus() {
      return this.quote?.Status
    },
    modalTitle() {
      if (this.type === 'approve') {
        if (this.quoteStatus === 'TO_APPROVE') {
          return this.i18n.t('/ManagerApproval/Modal/ApproveTitle')
        }
        if (this.quoteStatus === 'APPROVED') {
          if (this.quote.QuoteDetails.Cars[0].Type === 'CONFIGURATION') {
            return 'Order bestellen'
          }
          if (
            this.quote.QuoteDetails.Cars[0].Type === 'STOCKCAR' ||
            this.quote.QuoteDetails.Cars[0].Type === 'STOCKCAR_V2'
          ) {
            /*
              TODO:
              for orders with own stockcars “Klant koppelen”
              for orders not owned stockcars “Voertuig overnemen“
             */
            return 'Bestellen'
          }
        }
      }
      if (this.type === 'reject') {
        return this.i18n.t('/ManagerApproval/Modal/RejectTitle')
      }

      throw new Error("Can't determine the correct title for the popup")
    },
    isReasonRequired() {
      return this.type === 'reject'
    },
    reasonLabel() {
      if (this.type === 'approve') {
        if (this.quoteStatus === 'TO_APPROVE') {
          return this.i18n.t('/ManagerApproval/Modal/ApproveReasonLabel')
        }
        if (this.quoteStatus === 'APPROVED') {
          return 'Reden voor het bestellen(optioneel)'
        }
        if (
          this.quote.QuoteDetails.Cars[0].Type === 'STOCKCAR' ||
          this.quote.QuoteDetails.Cars[0].Type === 'STOCKCAR_V2'
        ) {
          /*
            TODO:
            for orders with own stockcars “Klant koppelen”
            for orders not owned stockcars “Voertuig overnemen“
           */
          return 'TODO label'
        }
      }

      if (this.type === 'reject') {
        return this.i18n.t('/ManagerApproval/Modal/RejectReasonLabel')
      }
      throw new Error("Can't determine the correct reason label for the popup")
    },
    reasonPlaceholder() {
      return this.type === 'approve'
        ? this.i18n.t('/ManagerApproval/Modal/ApproveReasonPlaceholder')
        : this.i18n.t('/ManagerApproval/Modal/RejectReasonPlaceholder')
    },
    confirmButtonText() {
      if (this.type === 'approve') {
        if (this.quoteStatus === 'TO_APPROVE') {
          return this.i18n.t('/ManagerApproval/Modal/ApproveButton')
        }
        if (this.quoteStatus === 'APPROVED') {
          return 'Bestellen'
        }
        if (
          this.quote.QuoteDetails.Cars[0].Type === 'STOCKCAR' ||
          this.quote.QuoteDetails.Cars[0].Type === 'STOCKCAR_V2'
        ) {
          /*
            TODO:
            for orders with own stockcars “Klant koppelen”
            for orders not owned stockcars “Voertuig overnemen“
           */
          return 'TODO label'
        }
      }
      if (this.type === 'reject') {
        return this.i18n.t('/ManagerApproval/Modal/RejectButton')
      }
      throw new Error("Can't determine the correct title for the confirm/reject button")
    },
    deliveryDateMessage() {
      if (this.type === 'approve' && this.deliveryDateChanged && this.newDeliveryDate) {
        return this.i18n.t('/ManagerApproval/Modal/DeliveryDateChanged', {
          date: this.newDeliveryDate,
        })
      }
      return null
    },
  },
  watch: {
    type() {
      this.reason = ''
      this.showError = false

      // Pre-fill delivery date message for approve
      if (this.type === 'approve' && this.deliveryDateMessage) {
        this.reason = this.deliveryDateMessage
      }
    },
  },
  mounted() {
    // Pre-fill delivery date message for approve
    if (this.type === 'approve' && this.deliveryDateMessage) {
      this.reason = this.deliveryDateMessage
    }
  },
  methods: {
    handleConfirm() {
      if (this.isReasonRequired && !this.reason.trim()) {
        this.showError = true
        return
      }
      console.log('handleConfirm ' + JSON.stringify(this.reason))
      this.showError = false
      this.$emit('confirm', this.reason.trim())
    },
  },
}
</script>

<style lang="scss" scoped>
.approval-modal-content {
  margin-bottom: 1.5rem;
}

.delivery-date-notice {
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 4px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  color: #1565c0;
  font-weight: 500;
}

.reason-input {
  display: flex;
  flex-direction: column;
}

label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
}

.required {
  color: #d32f2f;
}

textarea {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
}

textarea:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

textarea.error {
  border-color: #d32f2f;
  box-shadow: 0 0 0 2px rgba(211, 47, 47, 0.25);
}

.error-message {
  color: #d32f2f;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}
</style>
